import {
  type ForwardedRef,
  type JSX,
  type KeyboardEvent,
  lazy,
  type MouseEvent,
  Suspense,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';

import { type Optional } from 'utility-types';

import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';

import type * as types from './types';

import { useEventCleanup } from '../../hooks/useEventCleanup';
import { IconButton } from '../buttons/IconButton';
import { Popover, type PopoverProps } from '../floating/Popover';
import { type IconProp } from '../icons/types';

import { DropdownClonedTarget } from './DropdownClonedTarget';
import { useDropdownAnchorElement } from './hooks';
import { type MenuListProps } from './MenuList';
import { type ListModel, type ListValueMode } from './types';

const MenuList = lazy(() =>
  import('./MenuList').then(m => ({ default: m.MenuList })),
) as unknown as <
  T extends types.ListModel<C>,
  M extends types.ListValueMode,
  C extends types.ListItemComponent = types.ListItemComponent,
>({
  ref,
  shouldForceCreateContext,
  ...props
}: {
  readonly ref?: ForwardedRef<HTMLDivElement>;
} & MenuListProps<T, M, C>) => JSX.Element;

export interface DropdownProps<T extends ListModel = ListModel, M extends ListValueMode = never>
  extends
    Optional<Omit<PopoverProps, 'anchorEl' | 'children' | 'content'>, 'isOpen' | 'onClose'>,
    Pick<MenuListProps<T, M>, 'itemHeight' | 'minItemHeight' | 'mode'> {
  /**
   * The target, {@link types.DropdownTarget}, that the {@link Popover} is anchored to.  When
   * clicked, the target will cause the state of the {@link Popover} to change from "closed" to
   * "open".
   *
   * @see {@link types.DropdownTarget}
   */
  readonly children?: types.DropdownTarget;
  /**
   * The content that is rendered inside of the {@link Popover} for the {@link Dropdown} component.
   *
   * @see {@link types.DropdownContent}
   */
  readonly content: null | types.DropdownContent<T>;
  readonly getItemId?: (item: T) => string;
  /**
   * Defines the underlying icon, {@link IconProp}, that is rendered inside of the fallback
   * {@link IconButton} when the target, {@link types.DropdownTarget} is not provided.
   *
   * This prop is only applicable if the 'children' prop is not included.
   *
   * @default MoreVertOutlined
   */
  readonly icon?: IconProp;
  readonly isDisabled?: boolean;
  /**
   * Whether or not the {@link Popover} should be in an "open" state when the {@link Dropdown} is
   * initially rendered.
   *
   * This prop is not applicable if the open/closed state of the {@link Dropdown} is being
   * controlled externally to the component.
   */
  readonly isInitiallyOpen?: boolean;
  /**
   * A callback that is invoked when the state of the underlying {@link Popover} changes to "open".
   */
  readonly onOpen?: (e: MouseEvent<HTMLButtonElement | HTMLDivElement>) => void;
  /**
   * A callback that is invoked when the state of the underlying {@link Popover} changes in general.
   */
  readonly onOpenChange?: (isOpen: boolean) => void;
  /**
   * @default true
   */
  readonly shouldCloseOnClick?: boolean;
  /**
   * Whether or not re-clicking the target, {@link types.DropdownTarget}, while the {@link Dropdown}
   * is already "open" should close it.
   *
   * When 'false', the target only ever opens the {@link Dropdown}; once open, clicking the target
   * again is a no-op and the {@link Dropdown} closes only via an outside click, the "Escape" key or
   * item selection.
   *
   * @default true
   */
  readonly shouldCloseOnTargetClick?: boolean;
}

/**
 * A component that is responsible for rendering a {@link Popover} that is anchored by a target,
 * {@link types.DropdownTarget}, that conditionally hides/shows the content in the {@link Popover}
 * based on the "open"/"closed" state of this component.
 *
 * Whether or not the content, {@link types.DropdownContent}, is visible is determined by the
 * "open"/"closed" state of the component, which can be controlled both externally (similarly to a
 * "controlled input") and internally (similar to an "uncontrolled input").
 */
export const Dropdown = <T extends ListModel, M extends ListValueMode>({
  children,
  content: _content,
  getItemId,
  icon = MoreVertOutlined,
  isDisabled,
  isInitiallyOpen,
  isOpen: propIsOpen,
  itemHeight,
  minItemHeight,
  mode,
  onClose: propOnClose,
  onOpen,
  onOpenChange,
  shouldCloseOnClick = true,
  shouldCloseOnTargetClick = true,
  ...props
}: DropdownProps<T, M>) => {
  const contentId = useId();
  const [ref, anchorEl] = useDropdownAnchorElement();
  const [isInternalOpen, setIsInternalOpen] = useState(isInitiallyOpen ?? false);

  const withClose = useEventCleanup(() => setIsInternalOpen(false));

  const isOpen = propIsOpen ?? isInternalOpen;

  const handleClose = useCallback(() => {
    setIsInternalOpen(false);
    propOnClose?.();
    onOpenChange?.(false);
    anchorEl?.focus();
  }, [anchorEl, onOpenChange, propOnClose]);

  const handleToggle = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (isOpen) {
        if (shouldCloseOnTargetClick) {
          handleClose();
        }
      } else {
        setIsInternalOpen(true);
        onOpen?.(e);
        onOpenChange?.(true);
      }
    },
    [isOpen, handleClose, onOpen, onOpenChange, shouldCloseOnTargetClick],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement | HTMLUListElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        handleClose();
      }
    },
    [handleClose],
  );

  const renderProps = useMemo(
    () => ({
      'aria-controls': isOpen ? contentId : undefined,
      'aria-expanded': isOpen ? true : undefined,
      'aria-haspopup': 'menu' as const,
      'aria-label': 'open menu' as const,
      onClick: handleToggle,
      role: 'button' as const,
    }),
    [handleToggle, isOpen, contentId],
  );

  const content = useMemo(
    () => (Array.isArray(_content) ? _content.filter(item => item.isVisible !== false) : _content),
    [_content],
  );
  if (Array.isArray(content) && content.length === 0) {
    return null;
  }

  return (
    <>
      {typeof children === 'function' ? (
        children({ isOpen, props: { ...renderProps, isDisabled }, ref })
      ) : children ? (
        <DropdownClonedTarget {...renderProps} isDisabled={isDisabled} ref={ref}>
          {children}
        </DropdownClonedTarget>
      ) : (
        <IconButton.Large {...renderProps} icon={icon} isDisabled={isDisabled} ref={ref} />
      )}
      {/* The Popover cannot be rendered until the anchor (or target) element is present.  See the
          documentation for the 'useDropdownAnchorElement' hook for more information. */}
      {content !== null && anchorEl && (
        <Popover {...props} anchorEl={anchorEl} isOpen={isOpen} onClose={handleClose}>
          {typeof content === 'function' ? (
            content({
              close: handleClose,
              isOpen,
              props: { id: contentId, onKeyDown: handleKeyDown },
              withClose,
            })
          ) : Array.isArray(content) ? (
            <Suspense fallback={null}>
              <MenuList
                getItemId={getItemId}
                id={contentId}
                itemHeight={itemHeight}
                items={content.map(item => ({
                  ...item,
                  onClick: async e => {
                    await item.onClick?.(e);
                    if (shouldCloseOnClick) {
                      handleClose();
                    }
                  },
                }))}
                minItemHeight={minItemHeight}
                mode={mode}
                onKeyDown={handleKeyDown}
                shouldForceCreateContext
              />
            </Suspense>
          ) : (
            content
          )}
        </Popover>
      )}
    </>
  );
};
